import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { WidgetField } from "@/domains/buildings/types/buildingWidget";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { EnumMultiSelect } from "./EnumMultiSelect";
import { CLEAR_SENTINEL, enumSelections, isAllowedEnumValue } from "./enumValue";
import type { getEnumRule } from "./getEnumRule";

interface BuildingDataInspectorValueEditorProps {
  field: WidgetField;
  draft: string;
  enumRule: ReturnType<typeof getEnumRule>;
  /**
   * Confidence in the suggestion under review. Null when there is no single
   * suggestion to score — several sources disagree and none has been picked,
   * so the winner's own confidence would overstate what is actually known.
   */
  confidence: { level: number; label: string } | null;
  canManage: boolean;
  canSave: boolean;
  isSaving: boolean;
  pendingExtremeValue: string | null;
  tw: BuildingDataWidgetSections;
  onDraftChange: (value: string) => void;
  onSave: () => void;
  onSaveAnyway: () => void;
  onEditExtremeValue: () => void;
}

function ConfidenceMeter(props: {
  confidence: NonNullable<BuildingDataInspectorValueEditorProps["confidence"]>;
  tw: BuildingDataWidgetSections;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-muted-foreground">{props.tw.confidence}</span>
      <div className="flex items-center gap-2">
        <div
          className="flex gap-1"
          aria-label={`${props.tw.confidence}: ${props.confidence.label}`}
        >
          {[1, 2, 3].map((bar) => (
            <span
              key={bar}
              className={`h-2 w-6 rounded-sm ${bar <= props.confidence.level ? "bg-success" : "bg-muted/50"}`}
            />
          ))}
        </div>
        <span className="text-xs font-semibold text-muted-foreground">
          {props.confidence.label}
        </span>
      </div>
    </div>
  );
}

/**
 * The value reads as text until it is reached for, the way a row in the field
 * list does — a box drawn around it at rest states the obvious (it is editable)
 * over the one thing the pane exists to show. Hover and focus bring the border
 * back, so the affordance is there the moment it is wanted.
 */
const RESTING_CONTROL_CLASS =
  "h-9 min-w-0 flex-1 border-transparent bg-transparent hover:border-input focus:border-input data-[state=open]:border-input";

/** What an enum control needs, once `InspectorValueControl` has established there is a rule. */
type EnumControlProps = Pick<
  BuildingDataInspectorValueEditorProps,
  "field" | "draft" | "tw" | "onDraftChange"
> & { enumRule: NonNullable<BuildingDataInspectorValueEditorProps["enumRule"]> };

function EnumSelectControl(props: EnumControlProps & { disabled: boolean }) {
  const { enumRule, draft, disabled, tw, onDraftChange } = props;
  const selected = isAllowedEnumValue(draft, enumRule) ? draft.trim() : "";
  // A confirmed value is cleared by saving an empty draft, and the footer's
  // Reject is gone in that state — but this dropdown is the only thing that
  // writes the draft, so it has to carry the clear item itself, exactly as
  // `FieldRow`'s edit dropdown does. Without it a confirmed single-select
  // value can only be swapped for another option, never emptied.
  const canClear = props.field.state === "confirmed" && !!draft.trim();
  return (
    <Select
      value={selected}
      onValueChange={(picked) => onDraftChange(picked === CLEAR_SENTINEL ? "" : picked)}
      disabled={disabled}
    >
      <SelectTrigger id="bd-inspector-value" className={RESTING_CONTROL_CLASS}>
        <SelectValue placeholder={tw.selectAnOption} />
      </SelectTrigger>
      <SelectContent>
        {enumRule.options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
        {canClear ? (
          <SelectItem value={CLEAR_SENTINEL} className="text-muted-foreground">
            {tw.clearValue}
          </SelectItem>
        ) : null}
      </SelectContent>
    </Select>
  );
}

function EnumMultiSelectControl(props: Omit<EnumControlProps, "field">) {
  const { enumRule, draft, tw, onDraftChange } = props;
  const allowed = new Set(enumRule.options.map((option) => option.value));
  const selected = enumSelections(draft).filter((value) => allowed.has(value));
  return (
    <EnumMultiSelect
      options={enumRule.options}
      selected={selected}
      onChange={(values) => onDraftChange(values.join(","))}
      placeholder={tw.selectMultiple}
      emptyLabel={tw.noOptions}
      doneLabel={tw.done}
      cancelLabel={tw.cancel}
      defaultOpen={false}
      className={RESTING_CONTROL_CLASS}
    />
  );
}

function InspectorValueControl(props: BuildingDataInspectorValueEditorProps) {
  const { enumRule, draft, canManage, isSaving, tw, onDraftChange, onSave } = props;
  const disabled = !canManage || isSaving;
  if (enumRule?.kind === "enum") {
    return (
      <EnumSelectControl
        enumRule={enumRule}
        field={props.field}
        draft={draft}
        disabled={disabled}
        tw={tw}
        onDraftChange={onDraftChange}
      />
    );
  }
  if (enumRule?.kind === "enum_multiselect") {
    return (
      <EnumMultiSelectControl
        enumRule={enumRule}
        draft={draft}
        tw={tw}
        onDraftChange={onDraftChange}
      />
    );
  }
  return (
    <Input
      id="bd-inspector-value"
      value={draft}
      onChange={(event) => onDraftChange(event.target.value)}
      disabled={disabled}
      placeholder={tw.addValue}
      className={RESTING_CONTROL_CLASS}
      onKeyDown={(event) => {
        if (event.key === "Enter" && props.canSave) onSave();
      }}
    />
  );
}

function ExtremeValueWarning(props: BuildingDataInspectorValueEditorProps) {
  if (props.pendingExtremeValue === null) return null;
  return (
    <div role="alert" className="mt-3 rounded-md border border-warning/50 bg-warning/10 p-3">
      <div className="text-xs font-semibold text-foreground">{props.tw.extremeValueTitle}</div>
      <div className="mt-1 text-xs leading-5 text-foreground">{props.tw.extremeValueBody}</div>
      <div className="mt-2 flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7"
          disabled={props.isSaving}
          onClick={props.onEditExtremeValue}
        >
          {props.tw.extremeValueEdit}
        </Button>
        <Button size="sm" className="h-7" disabled={props.isSaving} onClick={props.onSaveAnyway}>
          {props.tw.extremeValueSaveAnyway}
        </Button>
      </div>
    </div>
  );
}

function ValueInput(props: BuildingDataInspectorValueEditorProps) {
  const { field, draft, canManage, canSave, isSaving, tw, onSave } = props;
  return (
    <>
      <label
        className="mt-4 block text-xs font-medium text-muted-foreground"
        htmlFor="bd-inspector-value"
      >
        {tw.valueLabel}
      </label>
      <div className="mt-1.5 flex gap-2">
        <InspectorValueControl {...props} />
        {field.unit ? (
          <span className="flex h-9 items-center rounded-md bg-muted/30 px-2 text-xs text-muted-foreground">
            {field.unit}
          </span>
        ) : null}
        {canManage ? (
          <Button
            size="sm"
            className="h-9"
            disabled={isSaving || !canSave || draft.trim() === field.value.trim()}
            onClick={onSave}
          >
            {isSaving ? <Loader2 size={14} className="animate-spin" /> : tw.save}
          </Button>
        ) : null}
      </div>
      <ExtremeValueWarning {...props} />
    </>
  );
}

export function BuildingDataInspectorValueEditor(props: BuildingDataInspectorValueEditorProps) {
  return (
    <section className="pb-4">
      {/* Confidence scores a machine suggestion awaiting review. A confirmed
          value is the user's own answer and a missing one has nothing to
          score, so the meter would read as "—" on both. */}
      {props.field.state === "pending" && props.confidence ? (
        <ConfidenceMeter confidence={props.confidence} tw={props.tw} />
      ) : null}
      <ValueInput {...props} />
    </section>
  );
}
